import '@fontsource-variable/inter'
import '@fontsource-variable/fraunces'
import '@fontsource-variable/jetbrains-mono'
import {createInertiaApp} from '@inertiajs/react'
import {createRoot} from 'react-dom/client'
import React, {ComponentType, ReactNode} from 'react'
import Layout from '../src/components/ui/layout'

type InertiaPageComponent = ComponentType<any> & {
    layout?: (page: ReactNode) => ReactNode
}

type PageModule = {
    default: InertiaPageComponent
}
const pageCache = new Map<string, Promise<PageModule>>()

export default createInertiaApp({
    resolve: async (name) => {
        // Make sure this matches the pattern expected by import.meta.glob
        const pagePath = `../pages/${name}.tsx`

        // Check cache first
        if (pageCache.has(pagePath)) {
            const cachedPromise = pageCache.get(pagePath)
            if (cachedPromise) {
                return await cachedPromise
            }
        }

        // Load all possible pages
        const pages = import.meta.glob<PageModule>('../pages/**/*.tsx')

        // Find the matching page
        const page = pages[pagePath]
        if (!page) {
            throw new Error(`Page not found: ${name}. Available paths: ${Object.keys(pages).join(', ')}`)
        }

        // Load the module and enhance it with default layout if needed
        const modulePromise = page().then(module => {
            const Component = module.default
            if (!Component.layout) {
                Component.layout = (page: ReactNode) => (
                    React.createElement(Layout, {children: page})
                )
            }
            return module
        })

        // Cache for future use
        pageCache.set(pagePath, modulePromise)
        return modulePromise
    },

    setup({el, App, props}) {
        if (!el) {
            throw new Error('Root element not found')
        }
        const root = createRoot(el)
        const app = React.createElement(App, props)
        root.render(app)
    },
})